import { OktaAuth } from "@okta/okta-auth-js";
import { oktaConfig } from "src/config/okta.config";
import { useAuthStore } from "src/stores/auth-store";
import { logger } from "src/utils";

class OktaAuthService {
  private oktaAuth: OktaAuth;

  constructor() {
    this.oktaAuth = new OktaAuth({
      issuer: oktaConfig.issuer,
      clientId: oktaConfig.clientId,
      scopes: oktaConfig.scopes,
      tokenManager: {
        storage: "cookie",
        // Secure cookie configuration
        secure: window.location.protocol === "https:",
      },
      cookies: {
        secure: true,
        sameSite: "lax",
      },
    });

    // Setup token manager event listeners for logging/monitoring
    this.setupTokenManagerListeners();
  }

  /**
   * Setup event listeners for token manager events
   */
  private setupTokenManagerListeners(): void {
    // Log token renewal events (includes rotation)
    this.oktaAuth.tokenManager.on("renewed", (key, newToken) => {
      logger.info(`[OktaAuth] Token ${key} renewed successfully`);

      // Requests read their Authorization header from the auth store, so the
      // store must follow every renewal whatever code path triggered it —
      // otherwise stale tokens keep being sent until the next explicit sync.
      if (key === "accessToken" && newToken && "accessToken" in newToken) {
        useAuthStore.getState().setAuthStoreState({
          accessToken: newToken.accessToken,
          expiresAt: newToken.expiresAt,
          isOktaLoginEnabled: true,
        });
      }

      if (key === "refreshToken" && newToken) {
        logger.info("[OktaAuth] Refresh token rotated");
      }
    });

    // Log token manager errors
    this.oktaAuth.tokenManager.on("error", (error) => {
      logger.error(
        `[OktaAuth] TokenManager error: ${error.message || String(error)}`,
      );
    });

    // Log token expiration events
    this.oktaAuth.tokenManager.on("expired", (key) => {
      logger.info(`[OktaAuth] Token ${key} expired`);
    });
  }

  /**
   * Authenticate user with username and password
   * This method is used when we want to authenticate programmatically
   * without using the widget
   */
  async authenticate(username: string, password: string) {
    try {
      const transaction = await this.oktaAuth.signInWithCredentials({
        username,
        password,
      });

      if (transaction.status === "SUCCESS") {
        const { tokens } = await this.oktaAuth.token.getWithoutPrompt({
          sessionToken: transaction.sessionToken,
        });

        // Store tokens
        this.oktaAuth.tokenManager.setTokens(tokens);

        return {
          success: true,
          tokens: {
            accessToken: tokens.accessToken?.accessToken,
            idToken: tokens.idToken?.idToken,
            refreshToken: tokens.refreshToken?.refreshToken,
            expiresAt: tokens.accessToken?.expiresAt,
          },
        };
      }

      return {
        success: false,
        error: "Authentication failed",
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Authentication failed",
      };
    }
  }

  /**
   * Get current tokens from token manager
   */
  async getTokens() {
    const accessToken = await this.oktaAuth.tokenManager.get("accessToken");
    const idToken = await this.oktaAuth.tokenManager.get("idToken");
    const refreshToken = await this.oktaAuth.tokenManager.get("refreshToken");

    return {
      accessToken,
      idToken,
      refreshToken,
    };
  }

  /**
   * Store tokens in the token manager
   * This should be used after login to ensure tokens are stored
   * in the same OktaAuth instance used for refresh operations
   *
   * @param tokens - The tokens object from Okta widget/auth flow
   */
  setTokens(tokens: Parameters<OktaAuth["tokenManager"]["setTokens"]>[0]) {
    logger.info("[OktaAuth] Storing tokens in token manager");
    this.oktaAuth.tokenManager.setTokens(tokens);
  }

  /**
   * Refresh tokens with rotation support
   * Returns the new access token with expiration info
   */
  async refreshAccessToken() {
    try {
      const token = await this.oktaAuth.tokenManager.renew("accessToken");

      if (token && "accessToken" in token) {
        logger.info("[OktaAuth] Access token refreshed successfully");
        return {
          success: true,
          token: {
            accessToken: token.accessToken,
            expiresAt: token.expiresAt,
          },
        };
      }

      return {
        success: false,
        error: "No access token returned after refresh",
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Token refresh failed";
      logger.error(`[OktaAuth] Token refresh failed: ${errorMessage}`);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get current access token with expiration info
   */
  async getAccessTokenWithExpiration(): Promise<{
    accessToken: string | null;
    expiresAt: number | null;
  }> {
    try {
      const token = await this.oktaAuth.tokenManager.get("accessToken");
      if (token && "accessToken" in token) {
        return {
          accessToken: token.accessToken,
          expiresAt: token.expiresAt,
        };
      }
      return { accessToken: null, expiresAt: null };
    } catch {
      return { accessToken: null, expiresAt: null };
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    return this.oktaAuth.isAuthenticated();
  }

  /**
   * Get user information from ID token
   */
  async getUser() {
    try {
      const user = await this.oktaAuth.getUser();
      return user;
    } catch {
      return null;
    }
  }

  /**
   * Clear all tokens
   */
  async clearTokens(): Promise<void> {
    // Passed explicitly so okta-auth-js keeps them stored until Okta answers
    const { accessToken, refreshToken } =
      this.oktaAuth.tokenManager.getTokensSync();
    const revocations = await Promise.allSettled([
      this.oktaAuth.revokeRefreshToken(refreshToken),
      this.oktaAuth.revokeAccessToken(accessToken),
    ]);
    revocations.forEach((revocation) => {
      if (revocation.status === "rejected") {
        // Do not block the logout process
        logger.error(
          `[OktaAuth] Token revocation failed: ${revocation.reason instanceof Error ? revocation.reason.message : String(revocation.reason)}`,
        );
      }
    });

    // Reset unlogged user state
    this.oktaAuth.tokenManager.clear();
    this.oktaAuth.transactionManager.clear();

    try {
      // Reset logged user state
      await this.oktaAuth.closeSession();
    } catch (error) {
      // Do not block the logout process
      logger.error(
        `Error closing session: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }
}

export const oktaAuthService = new OktaAuthService();
